// ====================================================================
// HEIGHTFIELD — the ground is a function, not an assumption
// ====================================================================
// The design plan's "decide flat-vs-hills NOW" item, decided: gentle hills.
// Every system that used to assume y=0 (player physics, creatures, camera,
// prop placement, tap-to-move) samples heightAt(x, z) instead.
//
// The field itself: a (GRID+1)² lattice of deterministic value noise,
// bilinear-sampled, shaped by two masks —
//   - the village plateau: dead flat inside r<16, blending to full hills by
//     r=26, so the existing village (±9) and its checkpoint logic sit on
//     exactly the flat ground they were built for;
//   - road corridors: the four cardinal roads and the two y=x diagonals are
//     pressed flat out to where their cobblestones end (~r 28), so paths stay
//     walkable and lanterns stay grounded.
// Max relief is ~2.4 units over ~15-unit wavelengths — rolling meadow, not
// platforming (a K–2 design constraint, not a technical one).
//
// Per-zone authored heightmaps later replace makeHeights() — heightAt() and
// its consumers don't change.
import * as THREE from 'three';

export const WORLD_HALF = 60;          // half the map's side length (map is ±60)
const GRID = 96;                       // cells per side → 1.25 units/cell
const STEP = (WORLD_HALF * 2) / GRID;

// Deterministic PRNG (mulberry32) — same hills on every load, every device.
function mulberry32(seed) {
  return function () {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const smoothstep = (t) => t * t * (3 - 2 * t);
const clamp01 = (t) => Math.max(0, Math.min(1, t));

// Scalar bilinear interpolation of the four corners (a,b = bottom row L/R;
// c,d = top row L/R) at fractional coords tx/tz. Allocation-free — this is on
// the heightAt hot path.
const bilerp = (a, b, c, d, tx, tz) => {
  const lo = a + (b - a) * tx;
  return lo + ((c + (d - c) * tx) - lo) * tz;
};

// One octave of value noise: random lattice values, smooth-interpolated.
function makeNoise(rand, cells, amplitude) {
  const n = cells + 1;
  const lattice = new Float32Array(n * n);
  for (let i = 0; i < lattice.length; i++) lattice[i] = rand() * amplitude;
  return (u, v) => {        // u, v in 0..1
    const fx = u * cells, fz = v * cells;
    const x0 = Math.min(cells - 1, Math.floor(fx));
    const z0 = Math.min(cells - 1, Math.floor(fz));
    const tx = smoothstep(fx - x0), tz = smoothstep(fz - z0);
    const a = lattice[z0 * n + x0],     b = lattice[z0 * n + x0 + 1];
    const c = lattice[(z0 + 1) * n + x0], d = lattice[(z0 + 1) * n + x0 + 1];
    return bilerp(a, b, c, d, tx, tz);
  };
}

function roadFlattenFactor(x, z) {
  // Distance to the nearest road centreline (only where that road exists).
  let d = Infinity;
  const r = Math.hypot(x, z);
  if (r < 30) {
    d = Math.min(d, Math.abs(x));               // N/S road
    d = Math.min(d, Math.abs(z));               // E/W road
    if (x * z > 0) d = Math.min(d, Math.abs(x - z) / Math.SQRT2);  // y=x diagonals
  }
  // The east road continues to Wordville (the word-games zone): keep its
  // corridor flat from the old road end to the zone plateau.
  if (x > 26 && x < 40) d = Math.min(d, Math.abs(z));
  // The west road continues out to the Grove (the oasis zone): mirror of the
  // Wordville corridor, so its cobbles stay grounded to the zone plateau.
  if (x < -26 && x > -40) d = Math.min(d, Math.abs(z));
  // The north road continues to Obby Peak (the obstacle-course zone): keep its
  // corridor flat from the old road end to the zone plateau.
  if (z > 26 && z < 40) d = Math.min(d, Math.abs(x));
  // The south road continues past the Village Green to Numberton (the numbers
  // zone): keep its corridor flat from the old road end out to the zone plateau
  // (mirror of the north/Obby corridor).
  if (z < -26 && z > -40) d = Math.min(d, Math.abs(x));
  // Flat within 2.2 of the centreline, full hills beyond 4.6.
  return smoothstep(clamp01((d - 2.2) / 2.4));
}

const heights = new Float32Array((GRID + 1) * (GRID + 1));
(function makeHeights() {
  const rand = mulberry32(7331);
  const octave1 = makeNoise(rand, 8, 1.0);     // ~15-unit wavelength
  const octave2 = makeNoise(rand, 18, 1.0);    // ~6.7-unit detail
  for (let iz = 0; iz <= GRID; iz++) {
    for (let ix = 0; ix <= GRID; ix++) {
      const x = -WORLD_HALF + ix * STEP;
      const z = -WORLD_HALF + iz * STEP;
      const u = ix / GRID, v = iz / GRID;
      let h = octave1(u, v) * 1.9 + octave2(u, v) * 0.5;
      // Village plateau: flat where the village was built flat.
      const r = Math.hypot(x, z);
      h *= smoothstep(clamp01((r - 16) / 10));
      // Wordville plateau: the word-games zone down the east road.
      const rw = Math.hypot(x - 44, z);
      h *= smoothstep(clamp01((rw - 12) / 8));
      // The Grove plateau: the oasis zone down the west road (mirror of above).
      const rg = Math.hypot(x + 44, z);
      h *= smoothstep(clamp01((rg - 12) / 8));
      // Obby Peak plateau: the obstacle-course zone up the north road.
      const ro = Math.hypot(x, z - 44);
      h *= smoothstep(clamp01((ro - 12) / 8));
      // Numberton plateau: the numbers zone down the south road (mirror of the
      // other three cardinal zones, all centred ±44 from the village).
      const rn = Math.hypot(x, z + 44);
      h *= smoothstep(clamp01((rn - 12) / 8));
      // Fishing Hole plateau: a small flat pocket on the SW diagonal for the
      // pond (world/villageDecor.js), so its water disc sits level instead of
      // tilting down the hillside.
      const rf = Math.hypot(x + 22, z + 20);
      h *= smoothstep(clamp01((rf - 3.2) / 4.5));
      // Brook channel: flatten a shallow east-west band where the south-road
      // brook runs (world/water.js), so its water + banks sit level under the
      // footbridge instead of draping over the meadow hills.
      const brookX = clamp01((12 - Math.abs(x)) / 3);            // 1 across the span, 0 by x≈15
      const brookZ = 1 - smoothstep(clamp01((Math.abs(z + 15.3) - 1.4) / 2.2));
      h *= 1 - brookX * brookZ;
      // South Green plateau: the cozy picnic terminus down the south road. A
      // small flat disc (flat to r9, hills by r16) so the green's spread of
      // beds, orchard and benches sits on flat ground.
      const rs = Math.hypot(x, z + 24);
      h *= smoothstep(clamp01((rs - 9) / 7));
      // Roads pressed flat out to where their cobblestones end.
      h *= roadFlattenFactor(x, z);
      heights[iz * (GRID + 1) + ix] = h;
    }
  }
})();

// ---- Sculpt overrides (editor only) -------------------------------------
// A sparse additive delta layer over the base lattice. The base noise stays
// immutable; sculpting writes here, heightAt() adds it back. deltaActive gates
// the extra sampling so the hot path (and every kid build, which never sculpts)
// pays nothing — this early-out is mandatory, heightAt is the single most
// called ground query in the game.
let deltas = null;          // lazily-allocated Float32Array, same lattice size
let deltaActive = false;

function ensureDeltas() {
  if (!deltas) deltas = new Float32Array((GRID + 1) * (GRID + 1));
  deltaActive = true;
}

function sampleHeightClamped(x, z) {
  const fx = Math.max(0, Math.min(GRID - 1e-6, (x + WORLD_HALF) / STEP));
  const fz = Math.max(0, Math.min(GRID - 1e-6, (z + WORLD_HALF) / STEP));
  const x0 = Math.floor(fx), z0 = Math.floor(fz);
  const tx = fx - x0, tz = fz - z0;
  const n = GRID + 1;
  const i00 = z0 * n + x0, i10 = i00 + 1, i01 = i00 + n, i11 = i01 + 1;
  const a = heights[i00], b = heights[i10], c = heights[i01], d = heights[i11];
  let h = bilerp(a, b, c, d, tx, tz);
  if (deltaActive) {
    const da = deltas[i00], db = deltas[i10], dc = deltas[i01], dd = deltas[i11];
    h += bilerp(da, db, dc, dd, tx, tz);
  }
  return h;
}

// Bilinear sample. The ONLY way any system should ask "how high is the
// ground here" — never assume 0. Outside the authored lattice, continue the
// edge slope gently instead of silently pinning all systems to one edge value.
export function heightAt(x, z) {
  const cx = Math.max(-WORLD_HALF, Math.min(WORLD_HALF, x));
  const cz = Math.max(-WORLD_HALF, Math.min(WORLD_HALF, z));
  let h = sampleHeightClamped(cx, cz);
  if (cx !== x || cz !== z) {
    const eps = STEP;
    const gx = (sampleHeightClamped(Math.min(WORLD_HALF, cx + eps), cz) -
                sampleHeightClamped(Math.max(-WORLD_HALF, cx - eps), cz)) /
               (Math.min(WORLD_HALF, cx + eps) - Math.max(-WORLD_HALF, cx - eps) || eps);
    const gz = (sampleHeightClamped(cx, Math.min(WORLD_HALF, cz + eps)) -
                sampleHeightClamped(cx, Math.max(-WORLD_HALF, cz - eps))) /
               (Math.min(WORLD_HALF, cz + eps) - Math.max(-WORLD_HALF, cz - eps) || eps);
    h += (x - cx) * gx + (z - cz) * gz;
  }
  return h;
}

// Apply one brush dab. mode: raise | lower | flatten | smooth. radius/strength
// in world units; raise/lower add an absolute delta scaled by a smoothstep
// falloff, flatten/smooth lerp toward a target with strength as the rate.
// Deliberately does NOT re-apply the village/road plateau masks — an author may
// want to sculpt the flat ground. Returns the dirty world AABB for redisplace.
export function sculpt(bx, bz, radius, strength, mode = 'raise') {
  if (!Number.isFinite(bx) || !Number.isFinite(bz) || !Number.isFinite(radius) || radius <= 0 || !Number.isFinite(strength)) {
    return { minX: bx, maxX: bx, minZ: bz, maxZ: bz };
  }
  ensureDeltas();
  const n = GRID + 1;
  const ix0 = Math.max(0, Math.floor((bx - radius + WORLD_HALF) / STEP));
  const ix1 = Math.min(GRID, Math.ceil((bx + radius + WORLD_HALF) / STEP));
  const iz0 = Math.max(0, Math.floor((bz - radius + WORLD_HALF) / STEP));
  const iz1 = Math.min(GRID, Math.ceil((bz + radius + WORLD_HALF) / STEP));
  const rate = clamp01(strength);
  const target = mode === 'flatten' ? heightAt(bx, bz) : 0;
  for (let iz = iz0; iz <= iz1; iz++) {
    for (let ix = ix0; ix <= ix1; ix++) {
      const x = -WORLD_HALF + ix * STEP;
      const z = -WORLD_HALF + iz * STEP;
      const dist = Math.hypot(x - bx, z - bz);
      if (dist > radius) continue;
      const w = smoothstep(clamp01(1 - dist / radius));   // 1 centre → 0 rim
      const idx = iz * n + ix;
      if (mode === 'raise')        deltas[idx] += strength * w;
      else if (mode === 'lower')   deltas[idx] -= strength * w;
      else if (mode === 'flatten') deltas[idx] += (target - (heights[idx] + deltas[idx])) * w * rate;
      else if (mode === 'smooth') {
        const l  = ix > 0    ? heights[idx - 1] + deltas[idx - 1] : heights[idx] + deltas[idx];
        const r  = ix < GRID ? heights[idx + 1] + deltas[idx + 1] : heights[idx] + deltas[idx];
        const u  = iz > 0    ? heights[idx - n] + deltas[idx - n] : heights[idx] + deltas[idx];
        const dn = iz < GRID ? heights[idx + n] + deltas[idx + n] : heights[idx] + deltas[idx];
        deltas[idx] += ((l + r + u + dn) / 4 - (heights[idx] + deltas[idx])) * w * rate;
      }
    }
  }
  return { minX: bx - radius, maxX: bx + radius, minZ: bz - radius, maxZ: bz + radius };
}

// Serialize/restore the sculpt layer as a sparse { latticeIndex: delta } map —
// only non-zero cells, for the edit layer (world/editLayer.js).
export function getHeightDeltas() {
  const out = {};
  if (!deltas) return out;
  for (let i = 0; i < deltas.length; i++) if (deltas[i] !== 0) out[i] = +deltas[i].toFixed(4);
  return out;
}
export function setHeightDeltas(obj) {
  ensureDeltas();
  deltas.fill(0);
  for (const k in obj) deltas[+k] = obj[k];
}
export function clearHeightDeltas() {
  if (deltas) deltas.fill(0);
  deltaActive = false;
}

// Displace a PlaneGeometry into the terrain surface. The plane is authored in
// XY and rotated -90° about X by the caller, so local (x, y) lands at world
// (x, -y) and local z becomes world height.
//
// Also bakes a per-vertex tint (independent noise octave + a sun-dried warmth
// on the hilltops). It multiplies into the grass material's diffuse — free at
// render time, anchored to world position, and one more layer that keeps the
// tiled detail texture from reading as a grid.
const tintNoise = makeNoise(mulberry32(4242), 13, 1.0);

// The per-vertex grass tint, factored out so the full displace and the editor's
// region re-displace bake byte-identical colors. Writes r,g,b into out[0..2].
export function vertexTint(x, z, h, out) {
  const u = clamp01((x + WORLD_HALF) / (WORLD_HALF * 2));
  const v = clamp01((z + WORLD_HALF) / (WORLD_HALF * 2));
  const t = tintNoise(u, v);                    // 0..1, ~9-unit wavelength
  const dry = clamp01(h / 2.2);                 // hilltops slightly sun-dried
  let r = 0.93 + t * 0.14 + dry * 0.06;
  let g = 0.93 + t * 0.14 + dry * 0.02;
  let b = 0.93 + t * 0.11;
  // Big, soft biome washes make each road/zone feel distinct without another
  // texture fetch: Wordville warms yellow, the Grove cools lush green, Obby
  // leans lavender stone, and the south green brightens meadow flowers.
  const zoneWash = (cx, cz, radius) => {
    const d = Math.hypot(x - cx, z - cz);
    return clamp01(1 - d / radius);
  };
  const word = zoneWash(43, 0, 28);
  const grove = zoneWash(-43, 0, 28);
  const obby = zoneWash(0, 43, 30);
  const green = zoneWash(0, -31, 22);
  const number = zoneWash(0, -46, 22);
  r += word * 0.07 + obby * 0.04 + green * 0.03 + number * 0.05;
  g += grove * 0.08 + green * 0.06 + number * 0.03;
  b += grove * 0.03 + obby * 0.09 + number * 0.06;
  out[0] = r; out[1] = g; out[2] = b;
}

const _tint = [0, 0, 0];

export function displaceGeometryToTerrain(geometry) {
  const pos = geometry.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), z = -pos.getY(i);
    const h = heightAt(x, z);
    pos.setZ(i, h);
    vertexTint(x, z, h, _tint);
    colors[i * 3] = _tint[0]; colors[i * 3 + 1] = _tint[1]; colors[i * 3 + 2] = _tint[2];
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
}

// Editor-only: re-displace just the vertices of an already-displaced terrain
// geometry that fall inside a world-space AABB (a sculpt brush's dirty box).
// Reads the geometry's actual segment count via pos.count, samples heightAt
// (so it picks up sculpt deltas and works for both the 64² LOW and 112² mesh),
// and updates the shared color attribute in place. playFloor and the shadow
// overlay share ONE geometry, so calling this once updates both. Recompute
// normals only on stroke-end (recomputeNormals=true) — it touches all verts.
export function redisplaceRegion(geometry, minX, maxX, minZ, maxZ, recomputeNormals = true) {
  const pos = geometry.attributes.position;
  const col = geometry.attributes.color;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), z = -pos.getY(i);
    if (x < minX || x > maxX || z < minZ || z > maxZ) continue;
    const h = heightAt(x, z);
    pos.setZ(i, h);
    if (col) { vertexTint(x, z, h, _tint); col.setXYZ(i, _tint[0], _tint[1], _tint[2]); }
  }
  pos.needsUpdate = true;
  if (col) col.needsUpdate = true;
  if (recomputeNormals) { geometry.computeVertexNormals(); geometry.computeBoundingSphere(); }
}
